/**
 * FLOYD 2026-10-26 (DEFECT 1) — the one prompt a superseded page shows (see src/lib/app/chunkLoadRecovery.ts).
 *
 *   'available' — a newer deployment is served (checked when the page regains focus and every 5 minutes):
 *                 offered, not forced.
 *   'required'  — a lazily loaded chunk of this build could not be loaded: the action that hit it was not carried
 *                 out, and others that need a chunk will fail the same way until the page is reloaded.
 *
 * The page never reloads by itself.
 */
import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ToastAction, type ToastActionElement } from '@/components/ui/toast';
import {
  checkForNewDeployment,
  getAppBuildStatus,
  reloadApp,
  subscribeAppBuildStatus,
  type AppBuildStatus,
} from '@/lib/app/chunkLoadRecovery';

export const DEPLOYMENT_CHECK_INTERVAL_MS = 5 * 60 * 1000;

export function AppUpdateRequiredBanner() {
  const [status, setStatus] = useState<AppBuildStatus>(getAppBuildStatus);
  useEffect(() => subscribeAppBuildStatus(setStatus), []);

  // Notice a deploy BEFORE anything fails: on returning to the tab, on focus, and on an interval.
  useEffect(() => {
    const check = () => { if (document.visibilityState === 'visible') void checkForNewDeployment(); };
    const t = setInterval(check, DEPLOYMENT_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
    };
  }, []);

  if (status === 'current') return null;
  const required = status === 'required';
  return (
    <div role={required ? 'alert' : 'status'} data-testid="app-update-required" data-status={status}
      className="fixed inset-x-0 top-0 z-[10001] flex flex-wrap items-center justify-center gap-3 border-b border-amber-500 bg-amber-50 px-4 py-2 text-sm text-amber-900 shadow dark:bg-amber-950 dark:text-amber-100">
      <span>
        {required
          ? 'OptiBay was updated while this page was open. Reload to continue — the action that failed just now was not carried out.'
          : 'A new version of OptiBay is available. Reload when you are ready, so every action keeps working.'}
      </span>
      <Button size="sm" onClick={reloadApp} data-testid="app-update-reload">
        <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
        Reload
      </Button>
    </div>
  );
}

/** The same action, for a toast that reports a failed step. */
export function reloadToastAction(): ToastActionElement {
  return <ToastAction altText="Reload the page" onClick={reloadApp}>Reload</ToastAction>;
}
